'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { Shield, ArrowLeft } from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';

type Lang = 'en' | 'ar' | 'fr';

const translations = {
  en: {
    dir: 'ltr' as const,
    back: 'Back to Home',
    badge: 'Privacy Policy',
    title: 'Your Privacy Matters',
    updated: 'Last updated: August 2026',
    sections: [
      {
        heading: '1. Information We Collect',
        body: 'We collect information you provide directly to us, such as when you create an account, enter financial data, or contact support. This includes your name, email address, financial assets, liabilities, goals, and usage data.',
      },
      {
        heading: '2. How We Use Your Information',
        body: 'We use the information we collect to provide, maintain, and improve our services, process transactions, send notifications, and respond to your requests. We never sell your personal data to third parties.',
      },
      {
        heading: '3. Data Security',
        body: 'We implement industry-standard security measures including AES-256 encryption at rest and TLS 1.3 in transit to protect your financial data. Your information is stored on secure servers with restricted access.',
      },
      {
        heading: '4. Data Sharing',
        body: 'We do not sell, trade, or rent your personal information to third parties. We may share anonymized, aggregated data for analytics purposes. We may disclose information if required by law.',
      },
      {
        heading: '5. Cookies',
        body: 'We use cookies and similar tracking technologies to enhance your experience, remember your preferences, and analyze usage patterns. You can control cookie settings through your browser.',
      },
      {
        heading: '6. Your Rights',
        body: 'You have the right to access, correct, or delete your personal data at any time. You may also request a copy of your data or withdraw consent. Contact us at support to exercise these rights.',
      },
      {
        heading: '7. Data Retention',
        body: 'We retain your data for as long as your account is active or as needed to provide services. If you delete your account, we will delete your data within 30 days, except where required by law.',
      },
      {
        heading: '8. Changes to This Policy',
        body: 'We may update this Privacy Policy from time to time. We will notify you of significant changes via email or in-app notification. Continued use of the service constitutes acceptance of the updated policy.',
      },
      {
        heading: '9. Contact Us',
        body: 'If you have any questions about this Privacy Policy, please contact us through the Support page.',
      },
    ],
  },
  ar: {
    dir: 'rtl' as const,
    back: 'العودة للرئيسية',
    badge: 'سياسة الخصوصية',
    title: 'خصوصيتك تهمنا',
    updated: 'آخر تحديث: أغسطس 2026',
    sections: [
      {
        heading: '١. المعلومات التي نجمعها',
        body: 'نجمع المعلومات التي تقدمها لنا مباشرةً، مثل عند إنشاء حساب أو إدخال بيانات مالية أو التواصل مع الدعم. يشمل ذلك اسمك وعنوان بريدك الإلكتروني وأصولك المالية والتزاماتك وأهدافك وبيانات الاستخدام.',
      },
      {
        heading: '٢. كيف نستخدم معلوماتك',
        body: 'نستخدم المعلومات التي نجمعها لتقديم خدماتنا وصيانتها وتحسينها، ومعالجة المعاملات، وإرسال الإشعارات، والرد على طلباتك. لا نبيع بياناتك الشخصية أبداً لأطراف ثالثة.',
      },
      {
        heading: '٣. أمان البيانات',
        body: 'نطبق معايير أمان صناعية تشمل تشفير AES-256 للبيانات المخزنة وبروتوكول TLS 1.3 أثناء النقل لحماية بياناتك المالية. تُخزَّن معلوماتك على خوادم آمنة ذات وصول مقيّد.',
      },
      {
        heading: '٤. مشاركة البيانات',
        body: 'لا نبيع معلوماتك الشخصية أو نتاجر بها أو نؤجرها لأطراف ثالثة. قد نشارك بيانات مجمّعة ومجهولة الهوية لأغراض تحليلية. قد نكشف عن المعلومات إذا طلب القانون ذلك.',
      },
      {
        heading: '٥. ملفات تعريف الارتباط',
        body: 'نستخدم ملفات تعريف الارتباط وتقنيات تتبع مماثلة لتحسين تجربتك وتذكر تفضيلاتك وتحليل أنماط الاستخدام. يمكنك التحكم في إعدادات ملفات تعريف الارتباط من خلال متصفحك.',
      },
      {
        heading: '٦. حقوقك',
        body: 'يحق لك الوصول إلى بياناتك الشخصية أو تصحيحها أو حذفها في أي وقت. يمكنك أيضاً طلب نسخة من بياناتك أو سحب موافقتك. تواصل معنا عبر صفحة الدعم لممارسة هذه الحقوق.',
      },
      {
        heading: '٧. الاحتفاظ بالبيانات',
        body: 'نحتفظ ببياناتك طالما حسابك نشط أو حسب الحاجة لتقديم الخدمات. إذا حذفت حسابك، سنحذف بياناتك خلال 30 يوماً، إلا ما يقتضيه القانون.',
      },
      {
        heading: '٨. التغييرات على هذه السياسة',
        body: 'قد نحدّث سياسة الخصوصية هذه من وقت لآخر. سنخطرك بالتغييرات الجوهرية عبر البريد الإلكتروني أو إشعار داخل التطبيق. يُعدّ الاستمرار في استخدام الخدمة قبولاً للسياسة المحدّثة.',
      },
      {
        heading: '٩. تواصل معنا',
        body: 'إذا كانت لديك أي أسئلة حول سياسة الخصوصية هذه، يرجى التواصل معنا عبر صفحة الدعم.',
      },
    ],
  },
  fr: {
    dir: 'ltr' as const,
    back: 'Retour à l\'accueil',
    badge: 'Politique de confidentialité',
    title: 'Votre vie privée compte',
    updated: 'Dernière mise à jour : Août 2026',
    sections: [
      {
        heading: '1. Informations que nous collectons',
        body: 'Nous collectons les informations que vous nous fournissez directement, par exemple lors de la création d\'un compte, de la saisie de données financières ou du contact avec le support. Cela inclut votre nom, adresse e-mail, actifs financiers, passifs, objectifs et données d\'utilisation.',
      },
      {
        heading: '2. Comment nous utilisons vos informations',
        body: 'Nous utilisons les informations collectées pour fournir, maintenir et améliorer nos services, traiter les transactions, envoyer des notifications et répondre à vos demandes. Nous ne vendons jamais vos données personnelles à des tiers.',
      },
      {
        heading: '3. Sécurité des données',
        body: 'Nous mettons en œuvre des mesures de sécurité standard, notamment le chiffrement AES-256 au repos et TLS 1.3 en transit, pour protéger vos données financières. Vos informations sont stockées sur des serveurs sécurisés à accès restreint.',
      },
      {
        heading: '4. Partage des données',
        body: 'Nous ne vendons, n\'échangeons ni ne louons vos informations personnelles à des tiers. Nous pouvons partager des données anonymisées et agrégées à des fins analytiques. Nous pouvons divulguer des informations si la loi l\'exige.',
      },
      {
        heading: '5. Cookies',
        body: 'Nous utilisons des cookies et des technologies de suivi similaires pour améliorer votre expérience, mémoriser vos préférences et analyser les modèles d\'utilisation. Vous pouvez contrôler les paramètres des cookies via votre navigateur.',
      },
      {
        heading: '6. Vos droits',
        body: 'Vous avez le droit d\'accéder, de corriger ou de supprimer vos données personnelles à tout moment. Vous pouvez également demander une copie de vos données ou retirer votre consentement. Contactez-nous via la page Support pour exercer ces droits.',
      },
      {
        heading: '7. Conservation des données',
        body: 'Nous conservons vos données aussi longtemps que votre compte est actif ou selon les besoins pour fournir les services. Si vous supprimez votre compte, nous supprimerons vos données dans les 30 jours, sauf obligation légale.',
      },
      {
        heading: '8. Modifications de cette politique',
        body: 'Nous pouvons mettre à jour cette Politique de confidentialité de temps en temps. Nous vous informerons des changements importants par e-mail ou notification in-app. L\'utilisation continue du service constitue une acceptation de la politique mise à jour.',
      },
      {
        heading: '9. Nous contacter',
        body: 'Si vous avez des questions sur cette Politique de confidentialité, veuillez nous contacter via la page Support.',
      },
    ],
  },
};

export default function PrivacyPage() {
  const [lang, setLang] = useState<Lang>('ar');
  const t = translations[lang];
  const isRtl = t.dir === 'rtl';

  return (
    <div className="min-h-screen bg-background" dir={t.dir}>
      {/* Nav */}
      <nav className="sticky top-0 z-40 border-b border-border/50 bg-background/80 backdrop-blur-xl">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link href="/welcome" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft size={16} className={isRtl ? 'rotate-180' : ''} />
            {t.back}
          </Link>
          <div className="flex items-center gap-2">
            <AppLogo size={24} />
            <span className="text-sm font-semibold text-foreground">Wealth<span className="text-primary">OS</span></span>
          </div>
          <div className="flex items-center gap-1 bg-muted/50 rounded-xl p-1">
            {(['en', 'ar', 'fr'] as Lang[]).map((l) => (
              <button
                key={l}
                onClick={() => setLang(l)}
                className={`px-3 py-1 rounded-lg text-xs font-semibold transition-all ${
                  lang === l ? 'bg-primary text-primary-foreground shadow' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {l.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-primary/30 bg-primary/10 mb-5">
            <Shield size={13} className="text-primary" />
            <span className="text-xs font-semibold text-primary">{t.badge}</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-foreground mb-3">{t.title}</h1>
          <p className="text-sm text-muted-foreground">{t.updated}</p>
        </div>

        {/* Sections */}
        <div className="space-y-6">
          {t.sections.map((section) => (
            <div key={section.heading} className="glass-card rounded-2xl p-6 border border-border">
              <h2 className="text-base font-bold text-foreground mb-3">{section.heading}</h2>
              <p className="text-sm text-muted-foreground leading-relaxed">{section.body}</p>
            </div>
          ))}
        </div>

        {/* Footer links */}
        <div className="mt-12 pt-8 border-t border-border/50 flex flex-wrap items-center justify-center gap-6 text-xs text-muted-foreground">
          <Link href="/privacy" className="text-primary font-semibold">Privacy</Link>
          <Link href="/terms" className="hover:text-foreground transition-colors">Terms</Link>
          <Link href="/cookie-policy" className="hover:text-foreground transition-colors">Cookie Policy</Link>
          <Link href="/refund-policy" className="hover:text-foreground transition-colors">Refund Policy</Link>
          <Link href="/support" className="hover:text-foreground transition-colors">Support</Link>
        </div>
      </main>
    </div>
  );
}
